import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject
} from "@angular/core";
import { Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { MatIcon } from "@angular/material/icon";
import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";
import { MomentModule } from "ngx-moment";
import { BookingNavigationService } from "@app/services/navigation/booking-navigation.service";
import { BookingConfirmComponent } from "@app/view/booking/booking-confirm/booking-confirm.component";
import { BookingOutcomeComponent } from "@app/view/booking/booking-outcome/booking-outcome.component";
import { BookingHistoryComponent } from "@app/view/booking/booking-history/booking-history.component";
import { AvailabilityVisitService } from "@app/services/http/availability-visit.service";
import { CalendarDateDisplayComponent } from "@app/view/shared/calendar-date-display/calendar-date-display.component";
import { VisitStore } from "@app/services/store/visit-store";
import { Visit_DetailProjection } from "@app/models/rest/visit";
import { VisitStatus } from "@app/models/enums/visit-status.enum";

@Component({
  selector: "app-visit-booking-button",
  templateUrl: "./visit-booking-button.component.html",
  styleUrls: ["./visit-booking-button.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MomentModule,
    MatIcon,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    CalendarDateDisplayComponent
  ]
})
export class VisitBookingButtonComponent implements OnChanges {
  @Input() visitId: number;
  @Input() visitStatus: string;
  @Input() appointmentStart?: string | Date;
  @Input() appointmentEnd?: string | Date;
  @Input() disabled = false;
  /**
   * True when the visit belongs to an Orchestrator/project job. All booking
   * actions (book, rebook, refer, cancel) must be performed in Orchestrator,
   * so only the read-only appointment display and booking history remain.
   */
  @Input() orchestrated = false;
  @Output() actionCompleted = new EventEmitter<void>();

  book = false;
  rebook = false;
  refer = false;
  cancel = false;
  loading = false;

  private dialog = inject(MatDialog);
  private router = inject(Router);
  private bookingNavigationService = inject(BookingNavigationService);
  private availabilityService = inject(AvailabilityVisitService);
  private visitStore = inject(VisitStore);

  get hasActions(): boolean {
    return this.book || this.rebook || this.refer || this.cancel;
  }

  get appointmentStartDate(): Date | null {
    return this.appointmentStart ? new Date(this.appointmentStart) : null;
  }

  get appointmentEndDate(): Date | null {
    return this.appointmentEnd ? new Date(this.appointmentEnd) : null;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["visitStatus"] || changes["orchestrated"]) {
      this.computeActions();
    }
  }

  private computeActions(): void {
    this.book = false;
    this.rebook = false;
    this.refer = false;
    this.cancel = false;

    if (this.orchestrated) {
      return;
    }

    switch (this.visitStatus) {
      case "NEW":
      case "REFERRED":
      case "AUTO":
        this.book = true;
        this.rebook = false;
        this.refer = true;
        this.cancel = true;
        break;
      case "FLOATING":
      case "BOOKED":
        this.book = false;
        this.rebook = true;
        this.refer = true;
        this.cancel = true;
        break;
      case "IN_PROGRESS":
      case "CANCELLED":
      case "COMPLETED":
        this.book = false;
        this.rebook = false;
        this.refer = false;
        this.cancel = false;
        break;
    }
  }

  navigateToBooking(): void {
    this.bookingNavigationService.setOriginalRoute(this.router.url);
    this.router.navigate(["/booking/visit/", this.visitId]).then();
  }

  cancelVisit(): void {
    const visitStub = {
      id: this.visitId,
      visitStatus: this.visitStatus as VisitStatus
    } as unknown as Visit_DetailProjection;

    const dialogRef = this.dialog.open(BookingConfirmComponent, {
      width: "450px",
      data: {
        mode: "EDIT",
        visit: visitStub,
        referralMode: false,
        cancellationMode: true
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }
      this.availabilityService
        .bookVisit({
          visitId: this.visitId,
          startTime: null,
          endTime: null,
          force: false,
          bookingReasonId: result.bookingReasonId,
          notes: result.notes,
          displayedSlotRanking: null,
          refer: false,
          cancel: true,
          tickets: null
        })
        .then((response) => {
          const outcomeRef = this.dialog.open(BookingOutcomeComponent, {
            data: {
              success: response.success,
              message: response.message,
              mode: "CANCEL"
            }
          });
          outcomeRef.afterClosed().subscribe(() => {
            if (response.success) {
              this.actionCompleted.emit();
            }
          });
        })
        .catch((reason) => {
          this.dialog.open(BookingOutcomeComponent, {
            data: {
              success: false,
              message: "Error in booking service call: " + reason
            }
          });
        });
    });
  }

  referVisit(): void {
    const visitStub = {
      id: this.visitId,
      visitStatus: this.visitStatus as VisitStatus
    } as unknown as Visit_DetailProjection;

    const dialogRef = this.dialog.open(BookingConfirmComponent, {
      width: "450px",
      data: {
        mode: "EDIT",
        visit: visitStub,
        referralMode: true,
        cancellationMode: false
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }
      this.availabilityService
        .bookVisit({
          visitId: this.visitId,
          startTime: null,
          endTime: null,
          force: false,
          bookingReasonId: result.bookingReasonId,
          notes: result.notes,
          displayedSlotRanking: null,
          refer: true,
          cancel: false,
          tickets: null
        })
        .then((response) => {
          const outcomeRef = this.dialog.open(BookingOutcomeComponent, {
            data: {
              success: response.success,
              message: response.message,
              mode: "REFER"
            }
          });
          outcomeRef.afterClosed().subscribe(() => {
            if (response.success) {
              this.actionCompleted.emit();
            }
          });
        })
        .catch((reason) => {
          this.dialog.open(BookingOutcomeComponent, {
            data: {
              success: false,
              message: "Error in booking service call: " + reason
            }
          });
        });
    });
  }

  bookingHistory(): void {
    this.visitStore
      .loadSingleProjection<Visit_DetailProjection>(
        Visit_DetailProjection,
        this.visitId,
        "detail"
      )
      .then((visit) => {
        this.dialog.open(BookingHistoryComponent, {
          data: { visit },
          width: "1400px"
        });
      });
  }
}
